import type { CanvasDocumentV3 as CanvasDocument, CanvasNodeV3 as CanvasNode } from "@seedance/contracts";

const maximumSnapshots = 100;

export interface CanvasHistoryState {
  past: CanvasDocument[];
  present: CanvasDocument;
  future: CanvasDocument[];
}

export type CanvasCommand =
  | {
      type: "move_nodes";
      positions: Array<{
        nodeId: string;
        from: { x: number; y: number };
        to: { x: number; y: number };
      }>;
    }
  | { type: "add_node"; node: CanvasNode }
  | { type: "replace_document"; document: CanvasDocument };

export function createCanvasHistory(document: CanvasDocument): CanvasHistoryState {
  return { past: [], present: document, future: [] };
}

function applyCommand(document: CanvasDocument, command: CanvasCommand): CanvasDocument {
  if (command.type === "replace_document") return command.document;
  if (command.type === "add_node") {
    return { ...document, nodes: [...document.nodes, command.node] };
  }
  const positions = new Map(
    command.positions.map(({ nodeId, to }) => [nodeId, to] as const)
  );
  return {
    ...document,
    nodes: document.nodes.map((node) => {
      const position = positions.get(node.id);
      return position ? { ...node, position } : node;
    })
  };
}

export function applyCanvasCommand(
  history: CanvasHistoryState,
  command: CanvasCommand
): CanvasHistoryState {
  const present = applyCommand(history.present, command);
  if (present === history.present) return history;
  return {
    past: [...history.past, history.present].slice(-maximumSnapshots),
    present,
    future: []
  };
}

export function undoCanvas(history: CanvasHistoryState): CanvasHistoryState {
  const previous = history.past.at(-1);
  if (!previous) return history;
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future].slice(0, maximumSnapshots)
  };
}

export function redoCanvas(history: CanvasHistoryState): CanvasHistoryState {
  const next = history.future[0];
  if (!next) return history;
  return {
    past: [...history.past, history.present].slice(-maximumSnapshots),
    present: next,
    future: history.future.slice(1)
  };
}
